// ============================================================
import { safeStorage } from './safeStorage';
//  Performance mode (2026-05-24)
//  Decide si el dispositivo recibe el tema "lite" (sin backdrop-filter
//  ni animaciones infinitas) o el tema completo "glass".
//
//  Fuentes de decisión, en orden:
//   1. Elección explícita del usuario (localStorage 'evaPerfMode').
//   2. Veredicto del vigilante de FPS de cargas previas ('evaPerfDetected').
//   3. Señales estáticas de hardware/preferencias.
//
//  El tema lite se activa añadiendo la clase `perf-lite` a <html>.
//  La detección temprana (sin parpadeo) vive en public/index.html;
//  este módulo la reutiliza para el toggle de ajustes y el watchdog.
// ============================================================

export type PerfMode = 'auto' | 'lite' | 'full';

const MODE_KEY = 'evaPerfMode';
const DETECTED_KEY = 'evaPerfDetected'; // 'low' | 'ok'

export function getPerfMode(): PerfMode {
  try {
    const v = safeStorage.getItem(MODE_KEY);
    return v === 'lite' || v === 'full' ? v : 'auto';
  } catch {
    return 'auto';
  }
}

/** Señales baratas disponibles antes de pintar. */
export function detectLowPerfStatic(): boolean {
  try {
    const nav = navigator as Navigator & { deviceMemory?: number };
    return (
      (typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency <= 4) ||
      (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 4) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      window.matchMedia('(update: slow)').matches
    );
  } catch {
    return false;
  }
}

/** ¿Eva considera que este dispositivo es de bajo rendimiento? */
export function isDeviceLowPerf(): boolean {
  try {
    const detected = safeStorage.getItem(DETECTED_KEY);
    if (detected === 'low') return true;
    if (detected === 'ok') return false;
  } catch {
    /* ignore */
  }
  return detectLowPerfStatic();
}

/** Aplica la clase perf-lite a <html> según el modo efectivo. */
export function applyPerfMode(): void {
  const mode = getPerfMode();
  const lite = mode === 'lite' || (mode === 'auto' && isDeviceLowPerf());
  document.documentElement.classList.toggle('perf-lite', lite);
}

/** Guarda la elección del usuario y la aplica al instante. */
export function setPerfMode(mode: PerfMode): void {
  try {
    safeStorage.setItem(MODE_KEY, mode);
  } catch {
    /* ignore */
  }
  applyPerfMode();
}

/**
 * Vigilante de FPS: mide los fotogramas reales durante unos segundos.
 * Si detecta jank sostenido, marca el dispositivo como lento y —en modo
 * automático— activa el tema lite en caliente. El veredicto se persiste
 * para que la siguiente carga arranque sin medir ni parpadear.
 *
 * Solo corre en modo 'auto' y si aún no hay veredicto previo.
 */
export function runFpsWatchdog(): void {
  try {
    if (safeStorage.getItem(DETECTED_KEY)) return;
    if (getPerfMode() !== 'auto') return;
  } catch {
    return;
  }
  if (typeof requestAnimationFrame !== 'function') return;

  const SAMPLE_MS = 1000;
  const MAX_WINDOWS = 3; // ~3 s de muestreo
  const FPS_THRESHOLD = 40;

  let frames = 0;
  let lowWindows = 0;
  let windows = 0;
  let windowStart = performance.now();

  const tick = (now: number) => {
    frames++;
    if (now - windowStart >= SAMPLE_MS) {
      const fps = (frames * 1000) / (now - windowStart);
      if (fps < FPS_THRESHOLD) lowWindows++;
      windows++;
      frames = 0;
      windowStart = now;
      if (windows >= MAX_WINDOWS) {
        const isLow = lowWindows >= 2; // mayoría de ventanas con jank
        try {
          safeStorage.setItem(DETECTED_KEY, isLow ? 'low' : 'ok');
        } catch {
          /* ignore */
        }
        if (isLow) applyPerfMode();
        return;
      }
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** Olvida el veredicto del watchdog (se volverá a medir en la próxima carga). */
export function resetPerfDetection(): void {
  try {
    safeStorage.removeItem(DETECTED_KEY);
  } catch {
    /* ignore */
  }
}
